import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Download, History } from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DataTable, KpiCard } from "@/components/admin/analytics/AnalyticsKit";
import {
  getImportJobRows,
  listImportJobs,
  type ImportJob,
} from "@/lib/admin/import/import.functions";
import { IMPORT_MODULES } from "@/lib/admin/import/registry";
import { downloadImportLog } from "@/lib/admin/import/workbook";

export const Route = createFileRoute("/_admin/admin/import-history")({
  component: ImportHistoryPage,
});

const ALL = "__all__";

function ImportHistoryPage() {
  const listFn = useServerFn(listImportJobs);
  const rowsFn = useServerFn(getImportJobRows);
  const [moduleFilter, setModuleFilter] = useState<string>(ALL);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "import-history", moduleFilter],
    queryFn: () =>
      listFn({
        data: moduleFilter === ALL ? { limit: 200 } : { module: moduleFilter, limit: 200 },
      }),
  });

  const jobs = useMemo(() => data ?? [], [data]);
  const totals = useMemo(
    () =>
      jobs.reduce(
        (acc, j) => ({
          runs: acc.runs + 1,
          created: acc.created + j.created_count,
          updated: acc.updated + j.updated_count,
          failed: acc.failed + j.failed_count,
        }),
        { runs: 0, created: 0, updated: 0, failed: 0 },
      ),
    [jobs],
  );

  async function download(job: ImportJob) {
    const rows = await rowsFn({ data: { jobId: job.id, onlyIssues: false } });
    downloadImportLog(`${job.module}-${job.id.slice(0, 8)}`, rows);
  }

  return (
    <div className="flex min-h-0 flex-col gap-4">
      <AdminPageHeader
        title="Import History"
        description="Every bulk import run across the admin panel — who ran it, what changed, and the full downloadable log."
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Import runs" value={String(totals.runs)} tone="neutral" icon={History} loading={isLoading} />
        <KpiCard label="Records created" value={String(totals.created)} tone="success" icon={History} loading={isLoading} />
        <KpiCard label="Records updated" value={String(totals.updated)} tone="info" icon={History} loading={isLoading} />
        <KpiCard label="Failed rows" value={String(totals.failed)} tone="danger" icon={History} loading={isLoading} />
      </div>

      <div className="flex items-center gap-2">
        <Select value={moduleFilter} onValueChange={setModuleFilter}>
          <SelectTrigger className="h-9 w-64">
            <SelectValue placeholder="All modules" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All modules</SelectItem>
            {Object.values(IMPORT_MODULES).map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DataTable<ImportJob>
        rows={jobs}
        loading={isLoading}
        exportName="import-history"
        minWidth={1100}
        searchOn={(j) => `${j.module} ${j.filename} ${j.created_by_email ?? ""}`}
        columns={[
          {
            key: "created_at",
            header: "Date",
            render: (j) => new Date(j.created_at).toLocaleString("en-IN"),
            sortValue: (j) => j.created_at,
          },
          {
            key: "module",
            header: "Module",
            render: (j) => IMPORT_MODULES[j.module]?.label ?? j.module,
            sortValue: (j) => j.module,
          },
          { key: "filename", header: "File", render: (j) => j.filename },
          {
            key: "mode",
            header: "Mode",
            render: (j) => (
              <Badge variant={j.dry_run ? "secondary" : "outline"}>
                {j.dry_run ? "Dry run" : j.mode.replace("_", " ")}
              </Badge>
            ),
            sortValue: (j) => j.mode,
          },
          {
            key: "created_by_email",
            header: "Imported by",
            render: (j) => j.created_by_email ?? "—",
          },
          { key: "total_rows", header: "Rows", align: "right", render: (j) => j.total_rows, sortValue: (j) => j.total_rows },
          { key: "created_count", header: "Created", align: "right", render: (j) => j.created_count, sortValue: (j) => j.created_count },
          { key: "updated_count", header: "Updated", align: "right", render: (j) => j.updated_count, sortValue: (j) => j.updated_count },
          { key: "skipped_count", header: "Skipped", align: "right", render: (j) => j.skipped_count, sortValue: (j) => j.skipped_count },
          {
            key: "failed_count",
            header: "Failed",
            align: "right",
            render: (j) => (
              <span className={j.failed_count > 0 ? "font-medium text-destructive" : ""}>
                {j.failed_count}
              </span>
            ),
            sortValue: (j) => j.failed_count,
          },
          {
            key: "duration_ms",
            header: "Duration",
            align: "right",
            render: (j) => `${(j.duration_ms / 1000).toFixed(1)}s`,
            sortValue: (j) => j.duration_ms,
          },
          {
            key: "log",
            header: "Log",
            render: (j) => (
              <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => void download(j)}>
                <Download className="h-3.5 w-3.5" aria-hidden />
                Download
              </Button>
            ),
            exportValue: () => "",
          },
        ]}
      />
    </div>
  );
}
